// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2026 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

/**
 * Configuration and types relating to the RNA (Risks and Needs Assessment) survey
 * for North Carolina.
 *
 * This file should only contain internal identifiers, not copy. All copy should be
 * in the @jii/translation library.
 */

// Types relating to copy

export type RNAPageCopy = {
  heading: string;
  description?: string;
};
export type RNAQuestionCopy = {
  question: string;
  customInvalidAnswerNotice?: string;
  placeholderText?: string;
};

// The RNA form is a sequence of pages. Each page has a section header and some questions.

export type RNAPageId =
  | "sectionBasicNeeds"
  | "sectionAlcoholDrugs"
  | "sectionAlcoholDrugs2"
  | "sectionChildhoodTrouble"
  | "sectionFamilyGrowingUp"
  | "sectionFamilyNow"
  | "sectionBehavior"
  | "sectionBehavior2"
  | "sectionBehavior3"
  | "sectionFriends"
  | "sectionLifeAreas";

export type RNAPageSpec = {
  id: RNAPageId;
  questions: RNAQuestionId[];
};
export const fullRNASpec: RNAPageSpec[] = [
  {
    id: "sectionBasicNeeds",
    questions: [
      "needsWorkSchoolSatisfied",
      "needsHasJobSkills",
      "needsImproveJobSchool",
      "needsRanOutOfMoney",
      "needsStruggledWithBills",
      "needsReliedOnOthersForMoney",
      "needsHasPermanentHousing",
      "needsSpecialEducation",
      "needsReadingDifficulty",
      "needsCalculatingChange",
      "needsMedicalPaymentHardship",
    ],
  },
  {
    id: "sectionAlcoholDrugs",
    questions: [
      "alcoholDrugsDaysOfUse",
      "alcoholDrugsMoreThan5Drinks",
      "alcoholDrugsTimeOfOffense",
      "alcoholDrugsTimeOfOffenseV2",
    ],
  },
  {
    id: "sectionAlcoholDrugs2",
    questions: [
      "alcoholDrugsArguments",
      "alcoholDrugsHungOver",
      "alcoholDrugsTrouble",
      "alcoholDrugsStopping",
    ],
  },
  {
    id: "sectionChildhoodTrouble",
    questions: [
      "childhoodSkippingSchool",
      "childhoodRunningAway",
      "childhoodFighting",
      "childhoodHavingWeapons",
      "childhoodForcingSexualActivities",
      "childhoodHurtingAnimalsOrPeople",
      "childhoodTearingProperty",
      "childhoodStartingFires",
      "childhoodLying",
      "childhoodStealing",
    ],
  },
  {
    id: "sectionFamilyGrowingUp",
    questions: [
      "familyLawTrouble",
      "familyFights",
      "familyProblemSolving",
      "familyPunished",
      "familyNoRules",
      "familyAnythingGoes",
    ],
  },
  {
    id: "sectionFamilyNow",
    questions: ["familyHappy", "familyUnderstands"],
  },
  {
    id: "sectionBehavior",
    questions: [
      "behaviorCantStop",
      "behaviorImpulsive",
      "behaviorTryGetInTrouble",
      "behaviorAngry",
    ],
  },
  {
    id: "sectionBehavior2",
    questions: [
      "behaviorStayOutOfTrouble",
      "behaviorThinkBeforeActing",
      "behaviorLoseTemper",
      "behaviorApologize",
      "behaviorBlurt",
    ],
  },
  {
    id: "sectionBehavior3",
    questions: [
      "behaviorWorldOwesBetter",
      "behaviorGetEven",
      "behaviorBadLuck",
      "behaviorAffectOthers",
      "behaviorControlSpeech",
      "behaviorNotPlanned",
      "behaviorLawbreaking",
    ],
  },
  {
    id: "sectionFriends",
    questions: [
      "friendsJusticeInvolved",
      "friendsGangMembers",
      "friendsCommittedCrime",
      "friendsDrugs",
      "friendsOrganizations",
      "friendsClose",
    ],
  },
  {
    id: "sectionLifeAreas",
    questions: [
      "lifeAreaBehavior",
      "lifeAreaEmployability",
      "lifeAreaAlcoholDrugs",
      "lifeAreaEducation",
      "lifeAreaEmployment",
      "lifeAreaFamilyFriends",
      "lifeAreaLifeSkills",
      "lifeAreaPhysicalMedical",
      "lifeAreaMentalHealth",
      "lifeAreaFinancial",
      "lifeAreaHousing",
      "lifeAreaTransportation",
      "lifeAreaLegalStatus",
      "lifeAreaCustom",
      "lifeAreaCustomV2",
    ],
  },
];

export const allRNAQuestions: RNAQuestionId[] = fullRNASpec.flatMap(
  (pageSpec) => pageSpec.questions,
);

// RNA questions have different formats. These correspond to different answer types,
// which are stored in the database differently.

const rnaRadioQuestionFormats = [
  "FREQUENCY",
  "DAYS_PER_WEEK_RADIO",
  "YES_NO",
  "RATIO",
  "SOBRIETY_RADIO",
] as const;
export type RNARadioQuestionFormat = (typeof rnaRadioQuestionFormats)[number];
export const isRNARadioFormat = (s: string): s is RNARadioQuestionFormat =>
  rnaRadioQuestionFormats.includes(s as RNARadioQuestionFormat);

export type RNAQuestionFormat =
  | RNARadioQuestionFormat
  | "SOBRIETY"
  | "DAYS_PER_WEEK_ENTRY"
  | "LIFE_AREA";

export type RNAQuestionId = keyof typeof rnaQuestionConfigRaw;

export type RNAQuestionConfig = {
  format: RNAQuestionFormat;
  optional?: boolean;

  // We don't show these questions to residents or count them as exported in writeback.
  // Some questions have changed copy or formatting, but we need to keep the old configs
  // and include the deprecated IDs in the page spec to correctly display answers
  // from old forms in the staff view.
  deprecated?: boolean;
  // This isn't used anywhere except for documentation purposes; it identifies when
  // a question is deprecated because a new question replaced it.
  newQuestionId?: string;
};

const rnaQuestionConfigRaw = {
  // Page 1 of the paper form

  needsWorkSchoolSatisfied: {
    format: "FREQUENCY",
  },
  needsHasJobSkills: {
    format: "FREQUENCY",
  },
  needsImproveJobSchool: {
    format: "FREQUENCY",
  },
  needsRanOutOfMoney: {
    format: "FREQUENCY",
  },
  needsStruggledWithBills: {
    format: "FREQUENCY",
  },
  needsReliedOnOthersForMoney: {
    format: "FREQUENCY",
  },
  needsHasPermanentHousing: {
    format: "FREQUENCY",
  },
  needsSpecialEducation: {
    format: "FREQUENCY",
  },
  needsReadingDifficulty: {
    format: "FREQUENCY",
  },
  needsCalculatingChange: {
    format: "FREQUENCY",
  },
  needsMedicalPaymentHardship: {
    format: "FREQUENCY",
  },

  // Page 2 of the paper form

  alcoholDrugsDaysOfUse: {
    format: "DAYS_PER_WEEK_RADIO",
  },
  alcoholDrugsMoreThan5Drinks: {
    format: "DAYS_PER_WEEK_ENTRY",
  },
  alcoholDrugsTimeOfOffense: {
    format: "SOBRIETY",
    deprecated: true,
    newQuestionId: "alcoholDrugsTimeOfOffenseV2",
  },
  alcoholDrugsTimeOfOffenseV2: {
    format: "SOBRIETY_RADIO",
  },
  alcoholDrugsArguments: {
    format: "FREQUENCY",
  },
  alcoholDrugsHungOver: {
    format: "FREQUENCY",
  },
  alcoholDrugsTrouble: {
    format: "FREQUENCY",
  },
  alcoholDrugsStopping: {
    format: "FREQUENCY",
  },

  // Page 3 of the paper form

  childhoodSkippingSchool: {
    format: "YES_NO",
  },
  childhoodRunningAway: {
    format: "YES_NO",
  },
  childhoodFighting: {
    format: "YES_NO",
  },
  childhoodHavingWeapons: {
    format: "YES_NO",
  },
  childhoodForcingSexualActivities: {
    format: "YES_NO",
  },
  childhoodHurtingAnimalsOrPeople: {
    format: "YES_NO",
  },
  childhoodTearingProperty: {
    format: "YES_NO",
  },
  childhoodStartingFires: {
    format: "YES_NO",
  },
  childhoodLying: {
    format: "YES_NO",
  },
  childhoodStealing: {
    format: "YES_NO",
  },

  familyLawTrouble: {
    format: "FREQUENCY",
  },
  familyFights: {
    format: "FREQUENCY",
  },
  familyProblemSolving: {
    format: "FREQUENCY",
  },
  familyPunished: {
    format: "FREQUENCY",
  },
  familyNoRules: {
    format: "FREQUENCY",
  },
  familyAnythingGoes: {
    format: "FREQUENCY",
  },

  // Page 4 of the paper form

  familyHappy: {
    format: "FREQUENCY",
  },
  familyUnderstands: {
    format: "FREQUENCY",
  },

  behaviorCantStop: {
    format: "FREQUENCY",
  },
  behaviorImpulsive: {
    format: "FREQUENCY",
  },
  behaviorTryGetInTrouble: {
    format: "FREQUENCY",
  },
  behaviorAngry: {
    format: "FREQUENCY",
  },
  behaviorStayOutOfTrouble: {
    format: "FREQUENCY",
  },
  behaviorThinkBeforeActing: {
    format: "FREQUENCY",
  },
  behaviorLoseTemper: {
    format: "FREQUENCY",
  },
  behaviorApologize: {
    format: "FREQUENCY",
  },
  behaviorBlurt: {
    format: "FREQUENCY",
  },

  // Page 5 of the paper form

  behaviorWorldOwesBetter: {
    format: "FREQUENCY",
  },
  behaviorGetEven: {
    format: "FREQUENCY",
  },
  behaviorBadLuck: {
    format: "FREQUENCY",
  },
  behaviorAffectOthers: {
    format: "FREQUENCY",
  },
  behaviorControlSpeech: {
    format: "FREQUENCY",
  },
  behaviorNotPlanned: {
    format: "FREQUENCY",
  },
  behaviorLawbreaking: {
    format: "FREQUENCY",
  },

  // Page 6 of the paper form
  friendsJusticeInvolved: {
    format: "RATIO",
  },
  friendsGangMembers: {
    format: "RATIO",
  },
  friendsCommittedCrime: {
    format: "RATIO",
  },
  friendsDrugs: {
    format: "RATIO",
  },
  friendsOrganizations: {
    format: "RATIO",
  },
  friendsClose: {
    format: "RATIO",
  },

  // Life Areas / Self-Assessment Survey (part 2 of the paper form)

  lifeAreaBehavior: {
    format: "LIFE_AREA",
  },
  lifeAreaEmployability: {
    format: "LIFE_AREA",
  },
  lifeAreaAlcoholDrugs: {
    format: "LIFE_AREA",
  },
  lifeAreaEducation: {
    format: "LIFE_AREA",
  },
  lifeAreaEmployment: {
    format: "LIFE_AREA",
  },
  lifeAreaFamilyFriends: {
    format: "LIFE_AREA",
  },
  lifeAreaLifeSkills: {
    format: "LIFE_AREA",
  },
  lifeAreaPhysicalMedical: {
    format: "LIFE_AREA",
  },
  lifeAreaMentalHealth: {
    format: "LIFE_AREA",
  },
  lifeAreaFinancial: {
    format: "LIFE_AREA",
  },
  lifeAreaHousing: {
    format: "LIFE_AREA",
  },
  lifeAreaTransportation: {
    format: "LIFE_AREA",
  },
  lifeAreaLegalStatus: {
    format: "LIFE_AREA",
  },
  lifeAreaCustom: {
    format: "LIFE_AREA",
    optional: true,
    deprecated: true,
    newQuestionId: "lifeAreaCustomV2",
  },
  lifeAreaCustomV2: {
    format: "LIFE_AREA",
  },
} satisfies Record<string, RNAQuestionConfig>;

// convenience export that is correctly typed
export const rnaQuestionConfig: Record<RNAQuestionId, RNAQuestionConfig> =
  rnaQuestionConfigRaw;

export const currentRNAQuestions: RNAQuestionId[] = allRNAQuestions.filter(
  (q) => !rnaQuestionConfig[q].deprecated,
);
export const requiredRNAQuestions: RNAQuestionId[] = currentRNAQuestions.filter(
  (q) => !rnaQuestionConfig[q].optional,
);

export const rnaTextQuestionConfig = Object.fromEntries(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  Object.entries(rnaQuestionConfig).filter(([_, config]) => {
    return (
      isRNARadioFormat(config.format) || config.format === "DAYS_PER_WEEK_ENTRY"
    );
  }),
);

export const rnaCheckboxQuestionConfig = Object.fromEntries(
  Object.entries(rnaQuestionConfig).filter(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ([_, config]) => config.format === "SOBRIETY",
  ),
);

export const rnaLifeAreaQuestionConfig = Object.fromEntries(
  Object.entries(rnaQuestionConfig).filter(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ([_, config]) => config.format === "LIFE_AREA",
  ),
);

// Hardcoded values provided to us by NC that we should use for writeback
export const writebackAnswers: Omit<
  Record<RNAQuestionFormat, Record<string, string>>,
  "LIFE_AREA"
> = {
  FREQUENCY: {
    NEVER: "0",
    RARELY: "1",
    SOMETIMES: "2",
    USUALLY: "3",
    ALWAYS: "4",
  },
  DAYS_PER_WEEK_RADIO: {
    ZERO: "0",
    ONE_TO_TWO: "1",
    THREE_TO_FIVE: "2",
    SIX_TO_SEVEN: "3",
  },
  YES_NO: {
    YES: "Y",
    NO: "N",
  },
  RATIO: {
    NONE: "0",
    SOME: "1",
    MOST: "2",
    ALL: "3",
  },
  SOBRIETY_RADIO: {
    SOBER: "0",
    JUST_ALCOHOL: "1",
    JUST_DRUGS: "2",
    BOTH: "3",
  },
  SOBRIETY: {
    SOBER: "0",
    JUST_ALCOHOL: "1",
    JUST_DRUGS: "2",
    BOTH: "3",
  },
  // maps digit strings "0" to "7" to themselves
  DAYS_PER_WEEK_ENTRY: Object.fromEntries(
    [...Array(8).keys()].map((i) => [`${i}`, `${i}`]),
  ),
};

export const writebackLifeAreaNames: Partial<Record<RNAQuestionId, string>> = {
  lifeAreaBehavior: "Behavior",
  lifeAreaEmployability: "Employability",
  lifeAreaAlcoholDrugs: "Alcohol&OtherDrugs",
  lifeAreaEducation: "Education",
  lifeAreaEmployment: "Employment",
  lifeAreaFamilyFriends: "Family/Friends",
  lifeAreaLifeSkills: "LifeSkills",
  lifeAreaPhysicalMedical: "Physical/Medical",
  lifeAreaMentalHealth: "MentalHealth",
  lifeAreaFinancial: "Financial",
  lifeAreaHousing: "Housing",
  lifeAreaTransportation: "Transportation",
  lifeAreaLegalStatus: "LegalStatus",
  lifeAreaCustomV2: "Other",
};
